"use client"

import { Button } from "@chakra-ui/react"
import { signIn, signOut } from "next-auth/react"
import { Suspense } from "react"

import { useAuth } from "@/contexts/AuthContext"
import { getCallbackUrl } from "@/lib/auth-utils"
import { SignOutIcon } from "@/components/icons"

interface GoogleSignInButtonProps {
  size?: "sm" | "md" | "lg"
  variant?: "solid" | "outline"
}

const GoogleSignInButtonInner = ({
  size = "md",
  variant = "outline",
}: GoogleSignInButtonProps) => {
  const { session, status } = useAuth()

  if (status === "loading") {
    return (
      <Button loading size={size} variant={variant}>
        Loading…
      </Button>
    )
  }

  if (session) {
    return (
      <Button
        colorPalette="red"
        onClick={(e) => {
          e.preventDefault()
          signOut()
        }}
        size={size}
        type="button"
        variant="outline"
      >
        <SignOutIcon />
        Sign Out
      </Button>
    )
  }

  return (
    <Button
      backgroundColor={"white"}
      backgroundImage={`url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJNMTcuNiA5LjJsLS4xLTEuOEg5djMuNGg0LjhDMTMuNiAxMiAxMyAxMyAxMiAxMy42djIuMmgzYTguOCA4LjggMCAwIDAgMi42LTYuNnoiIGZpbGw9IiM0Mjg1RjQiIGZpbGwtcnVsZT0ibm9uemVybyIvPjxwYXRoIGQ9Ik05IDE4YzIuNCAwIDQuNS0uOCA2LTIuMmwtMy0yLjJhNS40IDUuNCAwIDAgMS04LTIuOUgxVjEzYTkgOSAwIDAgMCA4IDV6IiBmaWxsPSIjMzRBODUzIiBmaWxsLXJ1bGU9Im5vbnplcm8iLz48cGF0aCBkPSJNNCAxMC43YTUuNCA1LjQgMCAwIDEgMC0zLjRWNUgxYTkgOSAwIDAgMCAwIDhsMy0yLjN6IiBmaWxsPSIjRkJCQzA1IiBmaWxsLXJ1bGU9Im5vbnplcm8iLz48cGF0aCBkPSJNOSAzLjZjMS4zIDAgMi41LjQgMy40IDEuM0wxNSAyLjNBOSA5IDAgMCAwIDEgNWwzIDIuNGE1LjQgNS40IDAgMCAxIDUtMy43eiIgZmlsbD0iI0VBNDMzNSIgZmlsbC1ydWxlPSJub256ZXJvIi8+PHBhdGggZD0iTTAgMGgxOHYxOEgweiIvPjwvZz48L3N2Zz4=)`}
      backgroundPosition={"12px 11px"}
      backgroundRepeat={"no-repeat"}
      border={"none"}
      borderRadius={"3px"}
      boxShadow={"0 -1px 0 rgba(0, 0, 0, .04), 0 1px 1px rgba(0, 0, 0, .25)"}
      color={"#757575"}
      css={{
        "&:active": {
          backgroundColor: "#eeeeee",
        },
        "&:disabled": {
          backgroundColor: "#ebebeb",
          boxShadow:
            "0 -1px 0 rgba(0, 0, 0, .04), 0 1px 1px rgba(0, 0, 0, .25)",
          cursor: " not-allowed",
          filter: "grayscale(100%)",
        },
        "&:focus": {
          boxShadow: `
          0 -1px 0 rgba(0, 0, 0, .04),
          0 2px 4px rgba(0, 0, 0, .25),
          0 0 0 3px #c8dafc`,
          outline: "none",
        },
        "&:hover": {
          boxShadow:
            "0 -1px 0 rgba(0, 0, 0, .04), 0 2px 4px rgba(0, 0, 0, .25)",
        },
      }}
      fontFamily={`-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen,Ubuntu,Cantarell,"Fira Sans","Droid Sans","Helvetica Neue",sans-serif`}
      fontSize={"14px"}
      fontWeight={"500"}
      // variant={variant}
      // color="social.google"
      // colorPalette="grey"
      onClick={() => {
        const { searchParams } = new URL(window.location.href)
        const callbackUrl = getCallbackUrl(searchParams)
        signIn("google", { callbackUrl })
      }}
      padding={"12px 16px 12px 42px"}
      size={size}
      transition={"background-color .3s, box-shadow .3s"}
      type="button"
    >
      Sign in with Google
    </Button>
  )
}

export const GoogleSignInButton = (props: GoogleSignInButtonProps) => (
  <Suspense
    fallback={
      <Button
        loading
        size={props.size ?? "md"}
        variant={props.variant ?? "outline"}
      >
        Loading…
      </Button>
    }
  >
    <GoogleSignInButtonInner {...props} />
  </Suspense>
)
